/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { html, rune, View } from 'rune-ts';
import { LiveDataType } from '../../../../../features/Live/type';
import { makeEmptyScreenNavigate } from '../../../../../shared/app/navigate';
import { lazyLoad } from '../../../../../shared/client/global';
import { typoPcMo } from '../../../../../shared/typography/typo';
import { classes, replaceNewlinesWithBr } from '../../../../../shared/util';
import { makeDefaultProfileImg } from '../../../../../shared/util/helper';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { getResizedUrl } from '../../../../../shared/util/image';
import klass from './CastOnAir.module.scss';
import { font } from '@services/MarppleShop/shared/typography';
import { fx } from '@fxts/core';
import { CardProductControllerView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';
import { ELang } from '@services/MarppleShop/shared/enum/ELang.ts';

export type CastOnAirProps = { lives: LiveDataType[] };

const default_img = makeDefaultProfileImg(200);

class CastOnAirBodyView extends View<LiveDataType[]> {
  active_live: LiveDataType;
  active_index: number;

  constructor(data: LiveDataType[]) {
    super(data);

    this.active_index = 0;
    this.active_live = data[this.active_index]!;
  }

  override template() {
    const { platform, iframe_url, products, title, description, label_intl } = this.active_live;
    const queryString = iframe_url.includes('?') ? '&' : '?';
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`<div class="${klass.content}">
      <div class="${klass.live_frame_container}">
        <div class="${klass.live_frame}">
          ${platform === 'youtube'
            ? html` <iframe
                data-src="${iframe_url}${queryString}mute=1&autoplay=1"
                class="don_lazy"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen="false"
              ></iframe>`
            : platform === 'afreecatv'
              ? html` <iframe
                  data-src="${iframe_url}${queryString}autoplay=true"
                  class="don_lazy"
                  frameborder="0"
                  allowfullscreen="false"
                ></iframe>`
              : html` <iframe
                  data-src="${iframe_url}${queryString}muted=true&autoplay=true"
                  class="don_lazy"
                  frameborder="0"
                  allowfullscreen="false"
                  scrolling="no"
                ></iframe>`}
        </div>
      </div>
      <div class="${klass.live_content_container}">
        <div class="${klass.live_content}">
          <div class="${klass.body}">
            <div class="${font('24_bold', '16_bold')}">
              ${html.preventEscape(replaceNewlinesWithBr(title))}
            </div>
            <div
              class="${classes(
                typoPcMo({ is_mobile, pc: '16_medium', mo: '12_medium' }),
                klass.description_text,
              )}"
            >
              ${html.preventEscape(replaceNewlinesWithBr(description))}
            </div>
          </div>
          ${this.active_live.shop_url && label_intl?.[ET.lang]
            ? new ButtonAction({
                href: this.active_live.shop_url,
                navigate: makeEmptyScreenNavigate(this.active_live.shop_url),
                size: is_mobile ? 'regular' : 'xMedium',
                type: 'blue',
                label: html`
                  <span class="${klass.button_label}">${html.preventEscape(label_intl?.[ET.lang])}</span>
                `,
                fit: !is_mobile,
              })
            : ''}
        </div>

        <ul class="${klass.product_list}">
          ${fx(products)
            .map(
              (product) =>
                new CardProductControllerView({
                  card_product_view_props: {
                    type: 'A',
                    size: is_mobile ? 'Xsmall' : 'Small',
                    layout: 'Horizontal',
                    theme: 'Light',
                  },
                  product_card_view_data: product,
                  options: {
                    resize_width: 600,
                  },
                }),
            )
            .take(4)
            .toArray()}
        </ul>
      </div>
    </div>`;
  }

  setActiveIndex(active_index: number) {
    this.active_live = this.data[active_index]!;
    this.active_index = active_index;
    this.redraw();
    lazyLoad();
  }
}

export class CastOnAir extends View<CastOnAirProps> {
  private active_index = 0;

  private cast_on_air_body_view = new CastOnAirBodyView(this.data.lives);

  protected override onRender() {
    this.delegate('click', `.${klass.tab_home_category}`, (e) => {
      const el = e.currentTarget as HTMLElement;
      const index = parseInt(el.dataset.index ?? '');

      if (isNaN(index) || index === this.active_index) return;

      this.active_index = index;

      this.element()
        .querySelectorAll(`.${klass.tab_home_category}`)
        .forEach((element, i) => element.classList.toggle(klass.selected, i === index));
      this.element()
        .querySelectorAll(`.${klass.tab_home_category_text}`)
        .forEach((element, i) => element.classList.toggle(klass.selected, i === index));

      this.cast_on_air_body_view.setActiveIndex(index);
    });
  }

  override template() {
    const { lives } = this.data;

    return html`<div class="${classes(klass.container, { [klass.container_padding]: ET.lang !== ELang.Kr })}">
      <div class="${klass.section_title}">
        <span class="${classes(klass.section_title_text, font('24_bold', '18_semibold'))}">
          ${ET('cast_on_air::creator_on_air')}
        </span>

        <div class="${klass.category_tab}">
          <div class="${klass.creator_category}">
            ${lives.map(
              (live, i) => html`
                <button
                  data-index="${i}"
                  data-active="${i === this.active_index}"
                  type="button"
                  class="${classes(klass.tab_home_category, { [klass.selected]: i === this.active_index })}"
                >
                  <img
                    src="${live.products?.[0]?.profile_image_url
                      ? getResizedUrl({
                          url: live.products?.[0]?.profile_image_url,
                          width: 200,
                          quality: 90,
                          format: 'webp',
                        })
                      : default_img}"
                    alt="profile"
                    class="${klass.banner_quick_image}"
                  />

                  <span
                    class="${classes(klass.tab_home_category_text, font('18_medium', '14_medium'), {
                      [klass.selected]: i === this.active_index,
                    })}"
                  >
                    <!-- @see https://github.com/marpple/marpple-web/blob/94b9652707eb9eafbc008d910754f2757b590afd/services/MarppleShop/features/Live/service/service.ts#L80 -->
                    ${live.products[0]!.store_name}
                  </span>
                </button>
              `,
            )}
          </div>
        </div>
      </div>

      ${this.cast_on_air_body_view}
    </div> `;
  }
}
